import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPut } from "../../services/api";
import type { OrganizationProfile } from "../../types";

const EMPTY_PROFILE: OrganizationProfile = {
  organizationId: "",
  companyName: "",
  legalName: "",
  industry: "",
  website: "",
  phone: "",
  email: "",
  address: {
    street: "",
    street2: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
  },
  taxId: "",
};

const INDUSTRIES = [
  "",
  "Retail",
  "Food & Beverage",
  "Healthcare",
  "Technology",
  "Manufacturing",
  "Professional Services",
  "Education",
  "Hospitality",
  "Construction",
  "Transportation",
  "Other",
];

export default function ProfileTab() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<OrganizationProfile>(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const orgId = user?.organizationId;

  useEffect(() => {
    if (!orgId) return;

    async function loadProfile() {
      try {
        const data = await apiGet<OrganizationProfile>(
          `/organizations/${orgId}`
        );
        setProfile(data);
      } catch (err) {
        if (!(err instanceof Error && err.message.includes("not found"))) {
          console.error("Failed to load profile:", err);
        }
        setProfile({ ...EMPTY_PROFILE, organizationId: orgId! });
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [orgId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name.startsWith("address.")) {
      const field = name.replace("address.", "");
      setProfile((prev) => ({
        ...prev,
        address: { ...prev.address, [field]: value },
      }));
    } else {
      setProfile((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setSaving(true);

    try {
      await apiPut(`/organizations/${orgId}`, profile);
      setStatus({ type: "success", message: "Profile saved successfully" });
    } catch (err) {
      setStatus({
        type: "error",
        message: err instanceof Error ? err.message : "Failed to save",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="settings-loading">Loading profile...</div>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="settings-section">
        <h2>Business Information</h2>
        <p className="section-desc">Basic details about your organization</p>

        <div className="settings-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="companyName">Company Name</label>
              <input
                id="companyName"
                name="companyName"
                type="text"
                value={profile.companyName}
                onChange={handleChange}
                placeholder="Acme Inc."
              />
            </div>
            <div className="form-group">
              <label htmlFor="legalName">Legal Name</label>
              <input
                id="legalName"
                name="legalName"
                type="text"
                value={profile.legalName}
                onChange={handleChange}
                placeholder="Acme Incorporated LLC"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="industry">Industry</label>
              <select
                id="industry"
                name="industry"
                value={profile.industry}
                onChange={handleChange}
              >
                {INDUSTRIES.map((ind) => (
                  <option key={ind} value={ind}>
                    {ind || "Select industry..."}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="taxId">Tax ID / EIN</label>
              <input
                id="taxId"
                name="taxId"
                type="text"
                value={profile.taxId}
                onChange={handleChange}
                placeholder="XX-XXXXXXX"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h2>Contact Information</h2>
        <p className="section-desc">
          How customers and partners can reach your business
        </p>

        <div className="settings-form">
          <div className="form-row-3">
            <div className="form-group">
              <label htmlFor="email">Business Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={profile.email}
                onChange={handleChange}
                placeholder="info@company.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={profile.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="form-group">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                type="url"
                value={profile.website}
                onChange={handleChange}
                placeholder="https://www.company.com"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h2>Business Address</h2>
        <p className="section-desc">
          Primary physical location of your business
        </p>

        <div className="settings-form">
          <div className="form-group">
            <label htmlFor="address.street">Street Address</label>
            <input
              id="address.street"
              name="address.street"
              type="text"
              value={profile.address.street}
              onChange={handleChange}
              placeholder="123 Main Street"
            />
          </div>

          <div className="form-group">
            <label htmlFor="address.street2">Apt, Suite, Unit (optional)</label>
            <input
              id="address.street2"
              name="address.street2"
              type="text"
              value={profile.address.street2}
              onChange={handleChange}
              placeholder="Suite 100"
            />
          </div>

          <div className="form-row-3">
            <div className="form-group">
              <label htmlFor="address.city">City</label>
              <input
                id="address.city"
                name="address.city"
                type="text"
                value={profile.address.city}
                onChange={handleChange}
                placeholder="New York"
              />
            </div>
            <div className="form-group">
              <label htmlFor="address.state">State / Province</label>
              <input
                id="address.state"
                name="address.state"
                type="text"
                value={profile.address.state}
                onChange={handleChange}
                placeholder="NY"
              />
            </div>
            <div className="form-group">
              <label htmlFor="address.zip">ZIP / Postal Code</label>
              <input
                id="address.zip"
                name="address.zip"
                type="text"
                value={profile.address.zip}
                onChange={handleChange}
                placeholder="10001"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address.country">Country</label>
            <select
              id="address.country"
              name="address.country"
              value={profile.address.country}
              onChange={handleChange}
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="JP">Japan</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
        </div>
      </div>

      <div className="settings-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
        {status && (
          <span className={`save-status ${status.type}`}>
            {status.message}
          </span>
        )}
      </div>
    </form>
  );
}
